import type { CheckOrderStatusStep, CreateOrderStep, LookupAssetStep, LookupCatalogueStep, ScheduleVisitStep, ShowCatalogueStep, ShowOrderStatusStep } from "./service-types";

/**
 * The summaries and the "still needs" hints of the after-sales steps (`lookup_asset`, `schedule_visit`) and the order steps
 * (`lookup_catalogue`, `check_order_status`, `show_catalogue`, `create_order`, `show_order_status`; M27 S2/S3). Pure, like `service-model.ts`.
 */
export type OrdersStep = LookupAssetStep | ScheduleVisitStep | LookupCatalogueStep | CheckOrderStatusStep | ShowCatalogueStep | CreateOrderStep | ShowOrderStatusStep;

type TFunction = (key: string, options?: Record<string, unknown>) => string;

const S = "engage.workflows.service.summaries";
const UNKNOWN = "engage.workflows.summaries.unknown";

/** Visit kinds a workflow may ask for (backend `VisitStepKind`). */
export const VISIT_STEP_KINDS = ["corrective", "pm", "calibration", "inspection"] as const;
export const VISIT_WINDOWS_MAX = 3;
export const VISIT_DURATION_MIN = 15;
export const VISIT_DURATION_MAX = 720;
export const VISIT_DURATION_DEFAULT = 120;
/** Where `lookup_catalogue` takes its search from, and where `check_order_status` takes its order (backend `LOOKUP_SOURCES`, `STATUS_SOURCES`). */
export const LOOKUP_CATALOGUE_SOURCES = ["query", "variable", "ticket_asset"] as const;
export const CHECK_ORDER_SOURCES = ["event", "variable", "latest"] as const;
export const LOOKUP_ASSET_SOURCES = ["event", "ticket", "variable"] as const;
export const QUERY_MAX = 200;
export const CATEGORY_MAX = 80;
export const PRODUCTS_MAX = 5;

const orUnknown = (value: string | undefined, t: TFunction): string => (value?.trim() ? value : t(UNKNOWN));

/** One line that says what one of these steps does, shown on its card. */
export function summarizeOrders(step: OrdersStep, t: TFunction): string {
  switch (step.type) {
    case "lookup_asset":
      return t(`${S}.lookup_asset_${step.source ?? "event"}`, { variable: orUnknown(step.variable, t) });
    case "schedule_visit":
      return t(`${S}.schedule_visit`, { kind: t(`engage.workflows.service.fields.visitKinds.${step.kind ?? "corrective"}`), windows: step.windows ?? VISIT_WINDOWS_MAX });
    case "lookup_catalogue":
      return t(`${S}.lookup_catalogue_${step.source ?? "query"}`, { query: orUnknown(step.query, t), variable: orUnknown(step.variable, t) });
    case "check_order_status":
      return t(`${S}.check_order_status_${step.source ?? "event"}`, { variable: orUnknown(step.variable, t) });
    case "show_catalogue":
      return t(step.query ? `${S}.show_catalogue_query` : `${S}.show_catalogue_message`, { query: step.query ?? "" });
    case "create_order":
      return t(`${S}.create_order`);
    case "show_order_status":
      return t(`${S}.show_order_status`);
  }
}

/** What `lookup_catalogue` still needs (the `builder.incomplete` key), or `null`. */
export function lookupCatalogueIncomplete(step: LookupCatalogueStep): string | null {
  if ((step.source ?? "query") === "query" && !(step.query ?? "").trim()) return "catalogueQuery";
  return step.source === "variable" && !step.variable ? "catalogueVariable" : null;
}

/** What `check_order_status` still needs, or `null`. */
export function checkOrderIncomplete(step: CheckOrderStatusStep): string | null {
  return step.source === "variable" && !step.variable ? "orderVariable" : null;
}
